import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
// Warn this many days before the one-time payment period ends.
const WARN_WITHIN_DAYS = 7;

/**
 * Aviso in-app de renovación para pagos únicos (PSE/Nequi): no hay recurrencia, así que el dueño
 * tiene que volver a pagar antes de que `subscription_end_date` pase. Solo los pagos únicos y los
 * pagos manuales del SUPERADMIN fijan `subscription_end_date` en un negocio activo; las suscripciones
 * recurrentes de Mercado Pago no, y los regalos (`is_gift`) se descartan aquí.
 *
 * El estado `pending` lo cubre `ActivationBanner`; una vez vencido y virado a `expired` por el cron,
 * también. Esta franja cubre la ventana "activo pero a punto de vencer" (y el hueco de <24 h entre
 * el vencimiento y la corrida del cron).
 */
const RenewalBanner = () => {
    const { user, business } = useContext(AuthContext);
    const { t } = useTranslation();
    const [dismissed, setDismissed] = useState(false);

    if (!user || !business) return null;
    // Solo el dueño puede pagar la suscripción; los colaboradores no.
    if (user.role !== 'owner') return null;
    if (business.is_gift) return null;
    if (!['active', 'trialing'].includes(business.subscription_status)) return null;
    if (!business.subscription_end_date) return null;

    const endDate = new Date(business.subscription_end_date);
    if (Number.isNaN(endDate.getTime())) return null;

    const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / MS_PER_DAY);
    if (daysLeft > WARN_WITHIN_DAYS) return null;

    const isExpired = daysLeft <= 0;
    const storageKey = `renewalBannerDismissed:${business.subscription_end_date}`;

    // El aviso de "vencido" no se puede descartar (las escrituras ya están bloqueadas de todos modos).
    let sessionDismissed = false;
    if (!isExpired) {
        try {
            sessionDismissed = sessionStorage.getItem(storageKey) === '1';
        } catch (e) {
            sessionDismissed = false;
        }
    }
    if (dismissed || sessionDismissed) return null;

    const handleDismiss = () => {
        setDismissed(true);
        try {
            sessionStorage.setItem(storageKey, '1');
        } catch (e) {
            /* sessionStorage no disponible — el aviso reaparece en la próxima carga */
        }
    };

    const bannerStyle = {
        background: isExpired
            ? 'linear-gradient(90deg, #b91c1c 0%, #dc2626 100%)'
            : 'linear-gradient(90deg, #b45309 0%, #d97706 100%)',
        color: 'white',
        borderBottom: `1px solid ${isExpired ? '#991b1b' : '#92400e'}`,
        position: 'relative',
        zIndex: 1,
    };

    const iconContainerStyle = {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        padding: '0.5rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    const ctaStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        borderRadius: '0.5rem',
        textDecoration: 'none',
        backgroundColor: 'white',
        color: isExpired ? '#b91c1c' : '#b45309',
        fontWeight: 'bold',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    };

    const message = isExpired
        ? t('renewalBanner.expiredDescription')
        : t('renewalBanner.description', { count: daysLeft });

    return (
        <div style={bannerStyle} className="shadow-sm">
            <Container className="py-3">
                <Row className="align-items-center gy-2">
                    <Col md={8} className="d-flex align-items-start align-items-md-center gap-3">
                        <div style={iconContainerStyle} className="flex-shrink-0">
                            <AlertTriangle size={24} color="white" />
                        </div>
                        <div>
                            <h5 className="mb-1 fw-bold">
                                {isExpired ? t('renewalBanner.expiredTitle') : t('renewalBanner.title')}
                            </h5>
                            <p className="mb-0 small text-white">{message}</p>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div className="d-flex gap-2 justify-content-md-end align-items-center">
                            <Link to="/settings/plan" style={ctaStyle}>
                                <span className="small">{t('renewalBanner.cta')}</span>
                                <ArrowRight size={16} />
                            </Link>
                            {!isExpired && (
                                <button
                                    type="button"
                                    onClick={handleDismiss}
                                    className="btn btn-sm btn-link text-white text-decoration-none"
                                    aria-label={t('renewalBanner.dismiss')}
                                >
                                    {t('renewalBanner.dismiss')}
                                </button>
                            )}
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default RenewalBanner;
